<template>
  <div>
    <ScrollBar
      id="items-rent"
      :minHeight="0"
      :maxHeight="150"
    >
      <SelectOptionPeriodRent
        v-show="!periodRentId"
        :showPeriodAll="false"
        :rentId="rentId"
        v-model="selectedPeriod"
      />
    </ScrollBar>

    <Loading
      type="line"
      :center="false"
      v-show="loading"
    />

    <ScrollBar
      id="items-rent"
      :minHeight="300"
      :maxHeight="300"
    >
      <table class="table table-responsive-xs">
        <colgroup>
          <col style="width: 80%" />
          <col style="width: 20%" />
        </colgroup>

        <thead>
          <tr>
            <th>
              <span class="title-header"> Produto </span>
            </th>
            <th class="text-center">
              <span class="title-header">Quantidade</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="main in grouped2">
            <tr :key="'main-' + main.key">
              <td
                class="group level-1"
                colspan="2"
              >
                <span class="title-group title">
                  <i class="fa-solid fa-layer-group icon-color"></i>
                  {{ main.name || "Sem grupo" }}
                </span>
              </td>
            </tr>

            <template v-for="group in main.groups">
              <tr :key="'group-' + group.key">
                <td
                  class="group level-2"
                  colspan="2"
                >
                  <span class="title-group title">
                    <i class="fa-regular fa-layer-group icon-color"></i>
                    {{ group.name || "Sem subgrupo" }}
                  </span>
                </td>
              </tr>

              <tr
                v-for="item in group.items"
                :key="'item-' + item.id"
              >
                <td class="td-product td">
                  <span>{{ item.productFullName }}</span>
                  <br />
                  <span
                    class="description"
                    v-html="item.description"
                  ></span>
                </td>

                <td class="text-center td-quantity td">
                  <div>
                    <span>{{ item.quantity }}</span>
                    <span v-if="item.type != 3">
                      ({{ item.unitOfMeasurementName }})
                    </span>
                  </div>

                  <div class="quantity">
                    <ItemsRentStatusStock
                      v-if="item.type != 3"
                      :data="item"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </tbody>

        <tbody v-show="content.totalRecords == 0">
          <tr>
            <td colspan="2">
              <span class="title">Nenhum registro encontrado!</span>
            </td>
          </tr>
        </tbody>
      </table>
    </ScrollBar>
  </div>
</template>

<script>
import SelectOptionPeriodRent from "../rental/period-rental/SelectOptionPeriodRent.vue";
import ScrollBar from "@nixweb/nixloc-ui/src/component/layout/ScrollBar.vue";
import TableTotalRecords from "@nixweb/nixloc-ui/src/component/shared/TableTotalRecords.vue";
import ItemsRentStatusStock from "../rental/items-rent/ItemsRentStatusStock.vue";
import Loading from "@nixweb/nixloc-ui/src/component/shared/Loading.vue";

import { mapGetters, mapActions } from "vuex";

export default {
  name: "ItemsList",
  props: {
    rentId: String,
    periodRentId: String,
  },
  components: {
    ScrollBar,
    TableTotalRecords,
    ItemsRentStatusStock,
    Loading,
    SelectOptionPeriodRent,
  },
  data() {
    return {
      urlGetApi: "/api/v1/rental/items-rent/get-all",
      selectedPeriod: {},
      loading: true,
      content: {
        data: [],
        totalRecords: 0,
      },
      baseParams: {
        search: undefined,
        filter: "contains",
        currentPage: 1,
        totalPerPage: 20,
      },
    };
  },
  mounted() {
    this.getOptional();
  },
  computed: {
    ...mapGetters("generic", ["event"]),

    grouped2() {
      const items = Array.isArray(this.content.data) ? this.content.data : [];

      const ordered = [...items].sort((a, b) => {
        const aMain = a.groupMainOrder ?? 999999;
        const bMain = b.groupMainOrder ?? 999999;
        if (aMain !== bMain) return aMain - bMain;

        const aGroup = a.groupOrder ?? 999999;
        const bGroup = b.groupOrder ?? 999999;
        if (aGroup !== bGroup) return aGroup - bGroup;

        const aItem = a.itemOrder ?? 999999;
        const bItem = b.itemOrder ?? 999999;
        if (aItem !== bItem) return aItem - bItem;

        return String(a.id || "").localeCompare(String(b.id || ""));
      });

      const mainMap = new Map();

      for (const item of ordered) {
        const mainName = item.groupMainName || "Sem grupo";
        const mainKey = item.groupMainName || "__sem_grupo_main__";

        if (!mainMap.has(mainKey)) {
          mainMap.set(mainKey, {
            key: mainKey,
            name: mainName,
            groupsMap: new Map(),
          });
        }

        const main = mainMap.get(mainKey);

        const groupName = item.groupRentName || "Sem subgrupo";
        const groupKey = item.groupRentName || "__sem_group_rent__";

        if (!main.groupsMap.has(groupKey)) {
          main.groupsMap.set(groupKey, {
            key: `${mainKey}::${groupKey}`,
            name: groupName,
            items: [],
          });
        }

        const group = main.groupsMap.get(groupKey);
        group.items.push(item);
      }

      return Array.from(mainMap.values()).map((main) => ({
        key: main.key,
        name: main.name,
        groups: Array.from(main.groupsMap.values()).map((group) => ({
          key: group.key,
          name: group.name,
          items: group.items,
        })),
      }));
    },
  },
  methods: {
    ...mapActions("generic", ["getApi"]),

    getAll(rentId, periodRentId) {
      this.loading = true;

      const params = {
        url: this.urlGetApi,
        obj: {
          ...this.baseParams,
          rentId: rentId,
          periodRentId: periodRentId,
          noPaginated: true,
          withAccessory: true,
          withOnlyStock: true,
        },
      };

      this.getApi(params).then((response) => {
        this.content.data = response.content.data;
        this.content.totalRecords = response.content.totalRecords;
        this.loading = false;
      });
    },

    getOptional() {
      const self = this;
      setTimeout(function () {
        const periodId = self.periodRentId ?? self.selectedPeriod?.period?.id;
        if (!periodId) return;
        self.getAll(self.rentId, periodId);
      }, 300);
    },
  },
  watch: {
    rentId: {
      handler() {
        this.getOptional();
      },
      deep: true,
    },
    selectedPeriod: {
      handler() {
        const self = this;
        setTimeout(function () {
          self.getOptional();
        }, 300);
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.table {
  width: 100% !important;
  table-layout: fixed;
}

.table th,
.table td {
  height: 10px !important;
  padding-left: 10px !important;
  padding-top: 7px !important;
  padding-bottom: 5px !important;
  padding-right: 10px !important;
  border-bottom: 0px !important;
}

.title-header {
  font-size: 14px;
  color: #757d8c;
  font-weight: 400;
  text-transform: uppercase;
}

.group {
  font-size: 14px;
  font-weight: 600;
  color: #111;
}

.icon-color {
  color: #2563eb;
  margin-right: 6px;
}

.level-1 {
  background-color: #e3e5ed;
}

.level-2 {
  background-color: #f6f6f8;
  padding-left: 18px !important;
}

.title-group {
  font-size: 14px;
}

.td-product {
  width: 80% !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.td-quantity {
  width: 20% !important;
}

.quantity {
  cursor: pointer;
}

.quantity:hover {
  text-decoration: underline;
}

.description {
  font-weight: 400;
  color: grey;
}

.without-group {
  font-size: 13px;
  font-style: italic;
  color: grey;
}
</style>
